import React, { useCallback, useReducer, useState, useMemo } from 'react';
import {
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Divider,
    Typography,
    Stack,
    Chip,
    Grid
} from '@mui/material';
import { ValidDataService } from '../../../../service/validDataService';
import { isLocalDev } from '../../../../utils/environment';
import useAbortController from '../../../../hooks/useAbortController';
import { useValidInventoryColumns } from '../useValidInventoryColumns';
import EnhancedTable from '../../../enhancedTable/EnhancedTable';

export default function FrameSelectDialog({ open, onClose, onSelect, locationId = null, locationName = '', statusFilter = null }) {
    const [signal, abortRequest] = useAbortController();
    const columns = useValidInventoryColumns('frame');
    const [selection, setSelection] = useState([]);
    const [state, updateState] = useReducer((state, params) => {
        return { ...state, ...params };
    }, {
        data: [],
        totalRowCount: 0,
        pageCount: 0,
        filter: '',
        loading: false,
    });

    // const columns = useMemo(
    //     () => [
    //     {
    //         Header: 'Model Number',
    //         accessor: 'modelNumber',
    //         enableFilter: true
    //     },
    //     {
    //         Header: 'Supplier',
    //         accessor: 'supplier.supplierName',
    //         enableFilter: true,
    //     },
    //     {
    //         Header: 'Brand',
    //         accessor: 'brand.name',
    //         enableFilter: true,
    //     },
    //     {
    //         Header: 'Material',
    //         accessor: 'properties.material',
    //         enableFilter: true,
    //     },
    //     {
    //         Header: 'Shape',
    //         accessor: 'properties.shape',
    //         enableFilter: true,
    //     },
    //     {
    //         Header: 'Gender',
    //         accessor: 'properties.gender',
    //         enableFilter: true,
    //     },        
    //     {
    //         Header: 'List Price',
    //         accessor: 'listPrice',
    //         enableFilter: true,
    //         type: 'number'
    //     },          
    //     ],
    //     []
    // );

    const fetchData = useCallback(({ pageSize, pageIndex, filters, sortBy }) => {
        const from = pageSize * pageIndex;
        const scopedFilters = [...(filters || [])];

        if (locationId) {
            scopedFilters.push(JSON.stringify({
                id: 'location_id',
                value: {
                    type: 'enum',
                    value: Number(locationId)
                }
            }));
        }

        if (statusFilter) {
            scopedFilters.push(JSON.stringify({
                id: 'status',
                value: {
                    type: 'enum',
                    value: statusFilter
                }
            }));
        }

        updateState({ loading: true });
        ValidDataService.getInventoryDataWithLimit(from, pageSize, scopedFilters, sortBy, 'frame', signal).then(result => {
            const { code, data } = result;
            if (code === 0) {
                updateState({
                    data: data.data,
                    totalRowCount: data.count,
                    pageCount: Math.ceil(data.count / pageSize),
                });
            }
            updateState({ loading: false });
        }).catch(error => {
            if (isLocalDev) {
                console.error(error)
            }
            updateState({ loading: false });
        });
    }, [locationId, signal, statusFilter]);

    const handleRowSelected = useCallback((rows) => {
        setSelection(rows);
    }, []);

    const handleSelect = () => {
        if (!selection?.length) return;
        onSelect(selection[0]);
        onClose();
    };

    const handleClose = (event, reason) => {
        // ignore the background click
        if (reason === 'backdropClick') {
            return;
        }
        abortRequest();
        onClose();
    };

    const selectedFrame = selection?.[0];

    const detailRows = useMemo(() => ([
        { label: 'Brand', value: selectedFrame?.brand?.name },
        { label: 'Model', value: selectedFrame?.modelNumber },
        { label: 'Color', value: selectedFrame?.properties?.color || selectedFrame?.color },
        { label: 'Size', value: selectedFrame?.properties?.size || selectedFrame?.size },
        { label: 'UPC / EAN', value: selectedFrame?.upc || selectedFrame?.upcOrEan },
        { label: 'Price', value: selectedFrame?.listPrice != null ? `$${Number(selectedFrame.listPrice).toFixed(2)}` : undefined },
        { label: 'Gender', value: selectedFrame?.properties?.gender },
        { label: 'Shape', value: selectedFrame?.properties?.shape },
        { label: 'Color Code', value: selectedFrame?.colorCode },
        { label: 'Temple Length', value: selectedFrame?.properties?.temple },
        { label: 'A', value: selectedFrame?.properties?.a },
        { label: 'B', value: selectedFrame?.properties?.b },
        { label: 'DBL', value: selectedFrame?.properties?.dbl },
        { label: 'ED', value: selectedFrame?.properties?.ed }
    ]), [selectedFrame]);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
            <DialogTitle>Select frame</DialogTitle>
            <DialogContent
                sx={{
                    // Critical: Explicitly enable vertical scrolling to fix Safari scroll issues
                    // Safari sometimes fails to recalculate scroll dimensions when async content loads
                    overflowY: 'auto',
                    
                    // Enable momentum/smooth scrolling on iOS and Safari
                    WebkitOverflowScrolling: 'touch',
                    
                    // Establish positioning context for the scroll container
                    position: 'relative',
                    
                    // Custom scrollbar styling for better visibility
                    '&::-webkit-scrollbar': {
                        width: 8,
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,.3)',
                        borderRadius: 4,
                    },
                }}            
            >
                {locationId && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {locationName ? `Showing frames for ${locationName}.` : 'Showing frames for the selected location.'}
                    </Alert>
                )}
                <Grid container spacing={2}>
                    <Grid item xs={12} md={7}>
                        <Typography variant="h5" sx={{ mb: 1 }}>Frames</Typography>
                        <Divider sx={{ mb: 2 }} />
                        <EnhancedTable
                            columns={columns}
                            data={state.data}
                            totalRowCount={state.totalRowCount}
                            pageCount={state.pageCount}
                            fetchData={fetchData}
                            loading={state.loading}
                            selectionMode="single"
                            onRowSelected={handleRowSelected}
                            actions={[]}
                        />
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, minHeight: 300 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>Selected frame</Typography>
                            <Divider sx={{ mb: 2 }} />
                            {selectedFrame ? (
                                <Stack spacing={1.5}>
                                    {detailRows.filter(r => r.value).map(row => (
                                        <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                            <Typography variant="body2" color="text.secondary">{row.label}</Typography>
                                            <Typography variant="body2" fontWeight={600}>{row.value}</Typography>
                                        </Box>
                                    ))}
                                    {!!selectedFrame?.tags?.length && (
                                        <Stack direction="row" spacing={1} flexWrap="wrap">
                                            {selectedFrame.tags.map(tag => (
                                                <Chip key={tag} size="small" label={tag} />
                                            ))}
                                        </Stack>
                                    )}
                                </Stack>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    Select a frame to see its details.
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleSelect} variant='contained' disabled={!selection?.length}>
                    Select
                </Button>
                <Button onClick={handleClose} color="primary">
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}